import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { fetchAllHouses } from '../redux/action/house';
import { messages } from '../common/notification';
import { loadHouseForPicker } from '../DeviceSchedule/api';
import { buildHouseOptions } from '../DeviceSchedule/function';
import { theme } from '../DeviceSchedule/constants';
import PropertySelect from '../DeviceSchedule/components/PropertySelect';
import { HomeIcon, RefreshIcon } from '../DeviceSchedule/components/Icons';
import '../DeviceSchedule/DeviceSchedule.css';
import { fetchConnectionStatus } from './api';
import { collectHouseDevices, matchesSearch, runPool, statusOf } from './function';
import ConnectionStatusTable from './components/ConnectionStatusTable';

/** How many status requests run at once when a whole property is checked. */
const STATUS_CONCURRENCY = 8;

function ConnectionStatus() {
  const housesList = useSelector((state) => state.housesList);
  const mutipleHouse = useSelector((state) => state.mutipleHouse);
  const login = useSelector((state) => state.login);

  const [houseId, setHouseId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  // deviceId -> { loading, connected, disconnectTime, disconnectReason, connectedSince, error }
  const [statuses, setStatuses] = useState({});
  const [checkedAt, setCheckedAt] = useState(null);
  const [checking, setChecking] = useState(false);

  // Bumped on every property change / page refresh so late responses from an
  // earlier run do not overwrite the current table.
  const runRef = useRef(0);

  const mobile = login?.profile?.mobile?.slice(3);
  const houseOptions = useMemo(() => buildHouseOptions(housesList), [housesList]);
  const house = houseId ? mutipleHouse[houseId] : null;
  const allDevices = useMemo(() => collectHouseDevices(house), [house]);

  useEffect(() => {
    if (!houseOptions.length && mobile) fetchAllHouses(mobile);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mobile]);

  const updateStatus = (deviceId, entry) =>
    setStatuses((prev) => ({ ...prev, [deviceId]: entry }));

  const checkDevice = async (deviceId, run) => {
    setStatuses((prev) => ({ ...prev, [deviceId]: { ...(prev[deviceId] || {}), loading: true } }));
    const res = await fetchConnectionStatus(deviceId);
    if (run !== runRef.current) return;
    updateStatus(
      deviceId,
      res.success ? { loading: false, ...res.data } : { loading: false, error: res.message }
    );
  };

  const checkAll = async (devices) => {
    const run = ++runRef.current;
    const ids = [...new Set(devices.map((device) => device.deviceId))];
    setStatuses(Object.fromEntries(ids.map((id) => [id, { loading: true }])));
    if (!ids.length) return;

    setChecking(true);
    await runPool(ids, STATUS_CONCURRENCY, (id) => checkDevice(id, run));
    if (run !== runRef.current) return;
    setChecking(false);
    setCheckedAt(Date.now());
  };

  const loadProperty = async (id, { force = false } = {}) => {
    if (!id || !mobile) return;
    if (!force && mutipleHouse[id]) return;
    setLoading(true);
    const res = await loadHouseForPicker(id, mobile);
    setLoading(false);
    if (res && res.success === false) {
      messages.error(res.message || 'Could not load this property');
    }
  };

  useEffect(() => {
    loadProperty(houseId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [houseId]);

  // Check every device whenever the device list of the selected property changes.
  useEffect(() => {
    if (!houseId || loading) return;
    checkAll(allDevices);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allDevices, houseId, loading]);

  const handleHouseChange = (nextId) => {
    runRef.current += 1;
    setHouseId(nextId);
    setSearch('');
    setFilter('all');
    setStatuses({});
    setChecking(false);
    setCheckedAt(null);
  };

  // Reloads the house (rooms/devices may have changed); the effect above then
  // re-checks every device once `loading` settles.
  const handleRefreshAll = () => loadProperty(houseId, { force: true });

  const handleRefreshDevice = (deviceId) => checkDevice(deviceId, runRef.current);

  const counts = useMemo(() => {
    const result = { all: allDevices.length, online: 0, offline: 0 };
    for (const device of allDevices) {
      const status = statusOf(statuses[device.deviceId]);
      if (status === 'online') result.online += 1;
      if (status === 'offline') result.offline += 1;
    }
    return result;
  }, [allDevices, statuses]);

  const visibleDevices = useMemo(
    () =>
      allDevices.filter(
        (device) =>
          matchesSearch(device, search) &&
          (filter === 'all' || statusOf(statuses[device.deviceId]) === filter)
      ),
    [allDevices, search, filter, statuses]
  );

  const roomCount = Array.isArray(house?.rooms) ? house.rooms.length : 0;
  const busy = loading || checking;

  return (
    <div className="deviceSchedulePage" style={{ width: '100%' }}>
      <div style={{ marginBottom: 18 }}>
        <div style={{ fontSize: 22, fontWeight: 800, color: '#3E3E3E', lineHeight: '28px' }}>Connection Status</div>
        <div style={{ fontSize: 13, color: theme.textMuted, marginTop: 3 }}>
          Check which devices on a property are connected right now.
        </div>
      </div>

      <div
        style={{
          background: theme.surface,
          border: `1px solid ${theme.border}`,
          borderRadius: 12,
          padding: '15px 18px',
          marginBottom: 16,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            marginBottom: 11,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
            <span
              style={{
                width: 28,
                height: 28,
                borderRadius: 8,
                background: theme.accentSoft,
                color: theme.accent,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <HomeIcon size={15} />
            </span>
            <div>
              <div style={{ fontSize: 13.5, fontWeight: 700, color: theme.text, lineHeight: '17px' }}>Property</div>
              <div style={{ fontSize: 11.5, color: theme.textFaint, lineHeight: '16px' }}>
                {house
                  ? `${roomCount} rooms · ${allDevices.length} devices` +
                    (checkedAt ? ` · checked ${moment(checkedAt).format('hh:mm:ss A')}` : '')
                  : 'Pick the property to check'}
              </div>
            </div>
          </div>

          {houseId && (
            <button
              type="button"
              onClick={handleRefreshAll}
              disabled={busy}
              title="Re-check every device on this property"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                border: `1px solid ${theme.border}`,
                background: theme.surface,
                color: theme.textMuted,
                borderRadius: 8,
                padding: '6px 11px',
                fontSize: 12,
                fontWeight: 700,
                fontFamily: theme.font,
                cursor: busy ? 'progress' : 'pointer',
              }}
            >
              <span className={busy ? 'dsSpin' : undefined} style={{ display: 'flex' }}>
                <RefreshIcon size={13} />
              </span>
              Refresh All
            </button>
          )}
        </div>

        <div style={{ maxWidth: 420 }}>
          <PropertySelect options={houseOptions} value={houseId} onChange={handleHouseChange} />
        </div>
      </div>

      <ConnectionStatusTable
        devices={visibleDevices}
        total={allDevices.length}
        counts={counts}
        loading={loading}
        hasHouse={!!houseId}
        statuses={statuses}
        search={search}
        onSearchChange={setSearch}
        filter={filter}
        onFilterChange={setFilter}
        onRefreshDevice={handleRefreshDevice}
      />
    </div>
  );
}

export default ConnectionStatus;
